(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const parseHexColor = (value) => {
    const hex = value.trim().replace('#', '');
    if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(hex)) return null;

    const normalized = hex.length === 3
      ? hex.split('').map((char) => char + char).join('')
      : hex;

    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16)
    };
  };

  const mixColor = (from, to, ratio) => ({
    r: Math.round(from.r + (to.r - from.r) * ratio),
    g: Math.round(from.g + (to.g - from.g) * ratio),
    b: Math.round(from.b + (to.b - from.b) * ratio)
  });

  const easeInOutSine = (value) => {
    const progress = clamp(value, 0, 1);
    return -(Math.cos(Math.PI * progress) - 1) / 2;
  };

  const toRgbString = ({ r, g, b }) => `rgb(${r} ${g} ${b})`;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setupSubpageDynamicBackground = () => {
    if (!document.querySelector('.sl-lookbook')) return;

    const root = document.documentElement;
    const styles = getComputedStyle(root);
    const base1 = parseHexColor(styles.getPropertyValue('--bg-base-1'));
    const base2 = parseHexColor(styles.getPropertyValue('--bg-base-2'));
    const base3 = parseHexColor(styles.getPropertyValue('--bg-base-3'));
    const gallery = document.querySelector('.sl-lookbook__gallery, .sl-lookbook__gallery-wrap');
    const footer = document.querySelector('.sl-footer');

    if (!base1 || !base2 || !base3) return;

    if (gallery && gallery.querySelector('.sl-lookbook__item')) {
      const galleryItems = Array.from(gallery.querySelectorAll('.sl-lookbook__item'));
      const galleryColors = [base1, base3, base2, base1, base3, base2];
      const sections = [
        ...galleryItems
          .filter((element, index) => index % 4 === 0)
          .map((element, index) => ({
            element,
            color: galleryColors[index % galleryColors.length]
          })),
        { element: footer, color: base2 }
      ].filter(({ element }) => element instanceof HTMLElement);
      if (!sections.length) return;

      const getDocumentTop = (element) => element.getBoundingClientRect().top + window.scrollY;
      let lastAppliedColor = '';
      const setBackgroundColor = (color) => {
        const rgb = toRgbString(color);
        if (rgb === lastAppliedColor) return;
        root.style.setProperty('--bg-dynamic-start', rgb);
        root.style.setProperty('--bg-dynamic-mid', rgb);
        root.style.setProperty('--bg-dynamic-end', rgb);
        lastAppliedColor = rgb;
      };
      const resolveColor = (scrollY) => {
        const transitionDistance = Math.max(1, window.innerHeight * 0.42);
        let currentColor = base2;

        for (const { element, color } of sections) {
          const point = getDocumentTop(element) - window.innerHeight * 0.45;
          const start = point - transitionDistance / 2;
          const end = point + transitionDistance / 2;

          if (scrollY < start) return currentColor;
          if (scrollY <= end) {
            const progress = clamp((scrollY - start) / Math.max(1, end - start), 0, 1);
            if (prefersReducedMotion()) {
              return mixColor(currentColor, color, Math.round(progress));
            }
            return mixColor(currentColor, color, easeInOutSine(progress));
          }

          currentColor = color;
        }

        return currentColor;
      };

      const updateBackground = () => {
        setBackgroundColor(resolveColor(window.scrollY));
      };

      let isTicking = false;
      const requestUpdate = () => {
        if (isTicking) return;
        isTicking = true;
        window.requestAnimationFrame(() => {
          isTicking = false;
          updateBackground();
        });
      };

      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
      window.addEventListener('load', requestUpdate);
      window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', requestUpdate);
      requestUpdate();
    }
  };

  const setupElementReveal = () => {
    const targets = Array.from(document.querySelectorAll('.sl-lookbook [data-element-reveal]'));
    targets.forEach((target) => target.classList.add('js-reveal-item'));
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const delay = Number(entry.target.getAttribute('data-reveal-delay'));
        window.setTimeout(() => entry.target.classList.add('is-visible'), delay);
        obs.unobserve(entry.target);
      });
    }, { threshold: 0, rootMargin: '0px -10%' });
    targets.forEach((target) => observer.observe(target));
  };

  const init = () => {
    setupSubpageDynamicBackground();
    setupElementReveal();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
